import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/report layout from the design system)
// Social Moderation — the web-admin review surface for Social Connect content.
// Follows AppealWorkflowReportPage: list → row click → portal review modal →
// decision → refetch.
//
// FOUR TABS, chosen from a switcher row of their own above the filters:
//   • Reported posts (the default) — the worklist. One row is one reported
//     TARGET, not one report: a brigaded post arrives as a single row carrying
//     its report count, and the modal is where the whole cluster of reporters
//     (and their reasons) is read. The report-STATUS control (Open / Removed /
//     No action / Author deleted / All reported) is a FILTER on this tab and
//     lives in the filters row, not up here — it narrows one view rather than
//     choosing between views.
//   • All posts — every post in the project, reported or not, live or already
//     removed. It exists because the queue is legitimately empty most of the
//     time, and an empty queue is indistinguishable from "I can't find the post
//     I was told about". Server-paged, because it spans the whole project.
//   • AI review — every post, edit, repost and comment the AI checked against the
//     Connect content policy, defaulting to the ones waiting for a moderator (held,
//     couldn't be checked, flagged in monitor mode). Its own table and modal
//     (components/social/AiReviewTab + AiReviewModal), for the reason DM has its own:
//     a check is a REVIEW ROW, not a (targetType, targetId) — a blocked attempt was
//     never saved, so there is nothing for getItem to open. Tab order: Reported
//     posts, AI review, All posts.
//   • Direct messages — WITHDRAWN. The tab is no longer offered to anyone; see
//     ALL_MOD_TABS for the decision and how to restore it. It showed REPORTED
//     private conversations only, never a browse-all view, because the
//     justification for All posts ("it was public to the project anyway") is
//     exactly what does not hold for private mail. Its code is still here and
//     still correct — only the way in is gone.
//     ⚠️ While it is withdrawn, a reported DM has NO review surface anywhere:
//     reports keep filing and keep setting a 400-day hold, and nothing actions
//     them. Either accept that, or turn DM reporting off in the mobile client.
// The first two share one table and one modal: takedown/restore identify their
// target from the content tables, never from a report, so they already worked on
// a post nobody reported — only the read path was missing. That modal renders
// the full comment thread with its own per-comment actions, so a single bad
// comment under an otherwise fine post can be removed without nuking the post —
// which is why All posts lists posts only.
//
// DM gets its OWN table and its OWN modal rather than a widened union: a DM row
// is a conversation between two people, not a (targetType, targetId) pair, and
// its endpoints key on a MESSAGE id while its rows are conversations.
//
// ACCESS — two separate grants, and the separation is the point:
//   • posts: `web.social_moderate` + project-wide data visibility. A 403 there
//     means the viewer is not a moderator at all, which is a permissions state
//     and not an error — hence the full-page AccessDeniedPanel.
//   • DM:    `web.social_dm_moderate` + project-wide data visibility, and a post
//     moderator does NOT inherit it. That grant is still enforced SERVER-side on
//     every /social-dm-moderation route — withdrawing the tab removed the door,
//     not the lock, so the API stays authoritative if any client calls it.
import ReportPageScaffold from '../components/ReportPageScaffold';
import { BlockLoader } from '../components/loading';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Download, X, Paperclip, Loader2, ShieldOff, Undo2, Pin, Eye, ShieldAlert, Users, Volume2, Plus } from 'lucide-react';
import FeedPostCard, { type FeedPost } from '../components/social/FeedPostCard';
import EngagementPanel, { EngagementCounts, type EngagementPanelPost } from '../components/social/EngagementPanel';
import type { EngagementTab } from '../services/engagementApi';
import PostComposerModal from '../components/social/PostComposerModal';
import AiReviewTab, { AiReviewFilterPills } from '../components/social/AiReviewTab';
import AiReviewModal, { AiVerdictPanel, outcomeSentence } from '../components/social/AiReviewModal';
import type {
  AiDecision, AiModeratorStatus, AiReviewCounts, AiReviewFilter, AiReviewOutcome, ReviewTargetType, Violation,
} from '../services/connectPolicyApi';
import { socialModerationAPI, socialDmModerationAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useReportFilters } from '../hooks/useReportFilters';
import { usePagination } from '../hooks/usePagination';
import { useTableSort, SortTh, type SortAccessors } from '../components/dashboard/useTableSort';
import AuthedImage, { AuthedLightbox, useAuthedMediaUrl } from '../components/AuthedImage';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import PaginationFooter from '../components/PaginationFooter';
import DateRangeFilter from '../components/DateRangeFilter';
import { NoProjectSelectedState } from '../components/NoProjectSelectedState';
import { AccessDeniedPanel } from '../components/AccessDeniedPanel';
import { buildCsv, downloadCsv } from '../utils/csv';

// ─────────────────────────────────────────────────────────────────────────────
// Types — mirror social-moderation.controller responses.

type TargetType = 'post' | 'comment';
// `live` only ever arrives from All posts. The reported queue cannot produce it:
// its rows exist because someone complained, so there is always a decision to
// name. Keeping both vocabularies in one union is what lets one STATUS_META, one
// pill and one modal serve both views.
//
// `held` / `not_approved` come from the Connect content policy: a post the AI held is
// saved but visible only to its author until a moderator approves it (All posts lists
// it so a moderator can find it), and `not_approved` is one a moderator then rejected.
// Neither is a removal — the post was never on anyone else's feed.
type ModStatus = 'open' | 'removed' | 'dismissed' | 'author_deleted' | 'live' | 'held' | 'not_approved';
type PostStatus = 'live' | 'removed' | 'author_deleted' | 'held' | 'not_approved';
/** Feed visibility under the Connect content policy. Absent from an API build that
 *  predates it, which is why every reader defaults it to 'published'. */
type ReviewState = 'published' | 'held' | 'rejected';

interface SocialAuthor {
  empCode: string;
  name: string;
  designation: string | null;
  storeName: string | null;
}

interface QueueRow {
  targetType: TargetType;
  targetId: string;
  postId: string;
  author: SocialAuthor;
  contentPreview: string;
  mediaCount: number;
  reportCount: number;
  openReports: number;
  topReason: string;
  status: ModStatus;
  createdAt: string | null;
  firstReportedAt: string | null;
  lastReportedAt: string | null;
  likeCount: number;
  commentCount: number;
  resolution: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  deletedReason: string | null;
}

/** A row of GET /social-moderation/posts. Deliberately carries no report-cluster
 *  fields (topReason, firstReportedAt, resolution…): the Reported view already
 *  answers those, and null-filling them here would put empty columns on every
 *  row of a table where almost nothing is reported. */
interface PostRow {
  targetType: 'post';
  targetId: string;
  postId: string;
  author: SocialAuthor;
  contentPreview: string;
  mediaCount: number;
  /** USER reports only — the server excludes the synthetic `admin_proactive`
   *  audit row a takedown writes, so this stays "was complained about" rather
   *  than quietly becoming a proxy for "was removed". */
  reportCount: number;
  openReports: number;
  status: PostStatus;
  createdAt: string;
  likeCount: number;
  commentCount: number;
  /** People who have seen it, once each (the author never). Optional for the same
   *  reason as the feed-view fields below: a web deploy can land ahead of the API,
   *  and a missing count renders as a dash, not as 0. */
  viewCount?: number;
  deletedAt: string | null;
  deletedBy: string | null;
  deletedReason: string | null;

  // ── Feed-view fields. The table reads none of these. ──
  // ⚠️ ALL OPTIONAL — they arrive only from an API build that includes the feed-view
  // fields. A web deploy landing ahead of the API serves rows without them, and typing
  // them as required is what let `post.media.length` crash the whole tab. See FeedPost.
  /** Whether the console user has already liked this post. */
  likedByMe?: boolean;
  /** FULL text. `contentPreview` above stays truncated for the table column — a card
   *  that stopped at 120 chars would read as a short post, not a clipped one. */
  content?: string;
  postType?: 'post' | 'announcement';
  isPinned?: boolean;
  audience?: string;
  /** The precise group a targeted post was aimed at ('level:th', 'mgr:…'), null
   *  otherwise. Only this console reads it; mobile labels off `audience`. */
  audienceTarget?: string | null;
  editedAt?: string | null;
  media?: Array<{ id: string; path: string; thumbnailPath: string | null }>;
}

/** Everything ReviewModal actually reads off its row. Both shapes satisfy it, so
 *  one modal serves both views without QueueRow's report-cluster fields having to
 *  be faked on a post nobody reported. */
type ReviewTarget = Pick<QueueRow, 'targetType' | 'targetId' | 'author'> & { status: ModStatus };

interface ModMedia {
  id: string;
  /** API-relative; the moderator variant, which CAN show already-removed content. */
  path: string;
  thumbnailPath: string | null;
  type: 'image';
  width: number | null;
  height: number | null;
}

interface ModPost {
  id: string;
  author: SocialAuthor;
  content: string | null;
  postType: 'post' | 'announcement';
  isPinned: boolean;
  likeCount: number;
  commentCount: number;
  /** Absent from an API build that predates it. */
  viewCount?: number;
  createdAt: string;
  deletedAt: string | null;
  deletedBy: string | null;
  deletedKind: string | null;
  deletedReason: string | null;
  restoredAt: string | null;
  restoredBy: string | null;
  media: ModMedia[];
  reviewState?: ReviewState;
  /** The line the author sees on a held / rejected post ("Waiting for a moderator —
   *  flagged for: …", or the moderator's rejection reason). */
  reviewNote?: string | null;
}

interface ModComment {
  id: string;
  author: SocialAuthor;
  content: string;
  createdAt: string;
  reportCount: number;
  deletedAt: string | null;
  deletedKind: string | null;
  deletedReason: string | null;
  /** True for the comment this queue row is about. */
  isTarget: boolean;
  reviewState?: ReviewState;
}

/** The latest AI check on this item, as getItem reports it (null = never checked, e.g.
 *  posted while the policy was Off). Deliberately thin — the full check, photos and the
 *  decision live in AiReviewModal, which this modal opens by `id`. `mode` / `targetType`
 *  are read if the server sends them and inferred otherwise (see ReviewModal). */
interface ModAiReview {
  /** A number from getItem today, a string from the AI review list (BIGSERIAL). Only
   *  ever passed back to the server, so either is accepted. */
  id: string | number;
  decision: AiDecision;
  outcome: AiReviewOutcome;
  violations: Violation[];
  summary: string | null;
  moderatorStatus: AiModeratorStatus;
  mode?: 'monitor' | 'enforce';
  targetType?: ReviewTargetType;
}

interface ModReport {
  id: number;
  reporter: { empCode: string; name: string };
  reasonCode: string;
  reasonText: string | null;
  status: string;
  resolution: string | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

interface ModItem {
  targetType: TargetType;
  targetId: string;
  post: ModPost;
  comments: ModComment[];
  reports: ModReport[];
  /** Optional: absent from an API build that predates the content policy. */
  aiReview?: ModAiReview | null;
}

// ── DM moderation — mirrors social-dm-moderation.controller ──────────────────

/** A row of GET /social-dm-moderation/queue. The unit is a CONVERSATION, not a
 *  message: the queue GROUPs the report table by conversation_id, so a harasser
 *  reported six times over two weeks is one row to adjudicate, not six.
 *
 *  `status` is the CASE status — the whole conversation, not one message — and
 *  reaches all four values: the queue LEFT JOINs social_dm_messages for the
 *  soft-delete quartet, so a case whose reported message was taken down reports
 *  'removed' rather than a green "No action". The server's ranking is open →
 *  removed → author_deleted → dismissed: outstanding work outranks a takedown,
 *  because one message being down does not retire the reports still open on the
 *  others. 'dismissed' therefore means closed AND nothing was ever removed.
 *
 *  There is NO `total` on the envelope, so this tab pages in memory. */
interface DmQueueRow {
  conversationId: string;
  /** The newest reported message. Load-bearing: takedown and dismiss key on a
   *  MESSAGE id, so this is the row's only actionable handle, and it is also the
   *  anchor the detail window centres on. */
  latestMessageId: string;
  reportCount: number;
  openReports: number;
  firstReportedAt: string | null;
  lastReportedAt: string | null;
  topReason: string;
  reportedEmpCode: string;
  reportedName: string;
  reporterEmpCode: string;
  reporterName: string;
  resolution: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  /** Distinct reported messages in the case that a moderator took down, and that
   *  any other hand deleted. "Closed, one message removed" and "closed, six
   *  removed" are different cases wearing the same chip. */
  removedMessages: number;
  authorDeletedMessages: number;
  status: DmCaseStatus;
}

/** The four values deriveDmCaseStatus can emit. A subset of ModStatus minus
 *  'live' — a reported conversation always has a decision to name, so there is
 *  no never-reported state to describe. */
type DmCaseStatus = 'open' | 'removed' | 'dismissed' | 'author_deleted';

interface DmParticipant {
  empCode: string;
  name: string;
  designation: string | null;
  storeName: string | null;
  leftAt: string | null;
}

interface DmMessage {
  id: string;
  seq: number;
  senderEmpCode: string;
  senderName: string;
  content: string;
  /** EPOCH MILLISECONDS, not an ISO string — unlike every other timestamp on
   *  this page. Formatted with fmtEpoch, never fmtDateTime. */
  createdAt: number | null;
  removed: boolean;
  removedBy: string | null;
  removedKind: string | null;
  removedReason: string | null;
  isReported: boolean;
  /** 'text' | 'image' | 'voice'. A reported VOICE NOTE has an empty `content`, so
   *  without these the row is a blank box wearing a REPORTED badge and the decision is
   *  taken blind — which is the one thing this queue exists to prevent. The server has
   *  always sent them; see getDmItem. */
  kind: string;
  hasAttachment: boolean;
  hasThumbnail: boolean;
  attachmentMime: string | null;
  attachmentBytes: number | null;
  attachmentDurationMs: number | null;
}

interface DmReport {
  id: number;
  messageId: string;
  reporterEmpCode: string;
  reporterName: string;
  reportedEmpCode: string;
  reportedName: string;
  reasonCode: string;
  reasonText: string | null;
  /** The message body AS REPORTED — kept even if the live message is later
   *  edited away or removed, so the evidence survives the takedown. */
  reportedBody: string | null;
  reportedSeq: number | null;
  status: string;
  resolution: string | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

interface DmItem {
  conversation: {
    id: string;
    projectId: number;
    createdBy: string;
    createdAt: string;
    legalHoldUntil: string | null;
  };
  participants: DmParticipant[];
  /** A BOUNDED ±25-message window around the anchor, never the whole thread.
   *  Soft-deleted messages are included and unfiltered — a taken-down message is
   *  the evidence. */
  messages: DmMessage[];
  /** USER reports only; the synthetic admin_proactive audit rows are stripped
   *  server-side. */
  reports: DmReport[];
  /** The likeliest DM abuse is a harasser reporting their victim FIRST, which
   *  reaches the queue as two symmetric rows a reviewer waves off as a mutual
   *  spat. The server states whether the other party also filed, and which came
   *  first, so the order is visible without reconstructing it. */
  counterReport:
    | { exists: false }
    | { exists: true; id: number; reporterEmpCode: string; filedAfterPrimary: boolean };
}

/** Everything DmReviewModal reads off its row. Separate from ReviewTarget on
 *  purpose — a DM has no targetType/targetId and would have to fake both. */
type DmReviewTarget = Pick<
  DmQueueRow,
  'conversationId' | 'latestMessageId' | 'reportedName' | 'reportedEmpCode' | 'status'
>;

// ─────────────────────────────────────────────────────────────────────────────
// Presentation helpers.

const STATUS_META: Record<ModStatus, { label: string; cls: string }> = {
  open: { label: 'Pending review', cls: 'bg-amber-100 text-amber-700' },
  removed: { label: 'Content removed', cls: 'bg-red-100 text-red-700' },
  dismissed: { label: 'No action', cls: 'bg-green-100 text-green-700' },
  author_deleted: { label: 'Author deleted', cls: 'bg-gray-100 text-gray-600' },
  // The quietest entry, on purpose: in All posts, live IS the norm. Giving every
  // healthy row a coloured chip would stop the two red ones reading as unusual.
  live: { label: 'Live', cls: 'bg-transparent text-gray-500' },
  // Amber like `open`: both mean "a moderator still has to decide". Not red — a held
  // post has not been removed from anything, it has not been shown yet.
  held: { label: 'Held for review', cls: 'bg-amber-100 text-amber-700' },
  not_approved: { label: 'Not approved', cls: 'bg-rose-50 text-rose-700' },
};

/** The VIEW. Was previously a sixth value smuggled into the report-status pill
 *  bar, which made "All posts" read as a fifth report status. */
type ModTab = 'reported' | 'ai_review' | 'all_posts' | 'dm';

/** Declaration order IS render order, exactly as DashboardPage's ALL_DASH_TABS.
 *
 *  ⚠️ 'dm' IS DELIBERATELY ABSENT — product decision, not an oversight. Admins are
 *  not to browse employees' private messages from this console. `tab` initialises
 *  to 'reported' and is neither persisted nor deep-linked, so leaving the value out
 *  of this array is what makes the view unreachable: `allowedTabs` derives from it,
 *  the tab strip renders from that, and nothing else can set the state to 'dm'.
 *
 *  The DM machinery below (types, state, queue fetch, DmReviewModal, sort
 *  accessors) is INTENTIONALLY LEFT IN PLACE and is now dead. It is a working,
 *  access-logged review surface that a policy change could want back; deleting it
 *  would mean rebuilding it. To restore, re-add the entry here and restore the
 *  probe in the effect marked DM PROBE below — nothing else needs to change.
 *
 *  ⚠️ CONSEQUENCE: with this view gone there is NO surface anywhere that actions a
 *  reported direct message. Reports still file, still set a 400-day legal hold, and
 *  now sit unreviewed forever. See the note on the probe effect. */
const ALL_MOD_TABS: { value: ModTab; label: string }[] = [
  { value: 'reported', label: 'Reported posts' },
  // Between the two worklists and the browse-all view: it is a queue too (its default
  // filter is "Needs review"), just one the AI fills instead of employees.
  { value: 'ai_review', label: 'AI review' },
  { value: 'all_posts', label: 'All posts' },
];

/** A FILTER on the Reported posts tab, not a view. Hidden on the other two tabs:
 *  All posts has no report cluster to filter, and the DM tab lists every case in
 *  its date window unfiltered — that queue is capped at 500 rows it pages in
 *  memory, so hiding cases behind a pill would cost a reviewer the one view that
 *  shows the whole caseload at once. */
const STATUS_TABS: { value: string; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'removed', label: 'Removed' },
  { value: 'dismissed', label: 'No action' },
  { value: 'author_deleted', label: 'Author deleted' },
  { value: 'all', label: 'All reported' },
];

// The report reason vocabulary (social-feed.service REPORT_REASON_CODES), plus
// `admin_proactive` — the synthetic report the backend writes when a moderator
// removes something nobody had reported, so every removal has an audit row.
const REASON_LABEL: Record<string, string> = {
  inappropriate: 'Inappropriate',
  harassment: 'Harassment',
  confidential_client_data: 'Confidential client data',
  competitor_or_pricing: 'Competitor / pricing',
  spam: 'Spam',
  other: 'Other',
  admin_proactive: 'Admin take-down',
};
const reasonLabel = (code: string | null | undefined): string =>
  !code ? '—' : REASON_LABEL[code] || code.replace(/_/g, ' ');

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const s = String(iso);
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
      });
};
/** DM message timestamps arrive as epoch MILLISECONDS, not ISO — the one place
 *  on this page where fmtDateTime would silently render "Invalid Date". */
const fmtEpoch = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
      });
};

/** A voice note's length as m:ss. Reported by the SENDING DEVICE'S timer, never
 *  measured from the file — so it is a label on the player, not a claim about it. */
const fmtMs = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '';
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** Coarse "how long has this been live" — the blast-radius signal that matters
 *  when deciding how urgently to act, so precision below an hour is noise. */
const fmtAge = (iso: string | null): string => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} hr`;
  return `${Math.round(hours / 24)} days`;
};

const pad2 = (n: number): string => String(n).padStart(2, '0');
const toDateStr = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayStr = (): string => toDateStr(new Date());
const daysAgoStr = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateStr(d);
};

// Module-level so the sort memo isn't invalidated on every render.
const QUEUE_SORT_ACCESSORS: SortAccessors<QueueRow> = {
  reported: (r) => r.lastReportedAt ?? '',
  type: (r) => r.targetType,
  author: (r) => r.author.name,
  preview: (r) => r.contentPreview,
  media: (r) => r.mediaCount,
  reports: (r) => r.reportCount,
  reason: (r) => reasonLabel(r.topReason),
  status: (r) => STATUS_META[r.status]?.label ?? r.status,
  decided: (r) => r.resolvedAt ?? '',
};

/** Its own map, not a widening of QUEUE_SORT_ACCESSORS: that one reads
 *  author.name / contentPreview / mediaCount / targetType, and a DM queue row
 *  carries none of the four. */
const DM_SORT_ACCESSORS: SortAccessors<DmQueueRow> = {
  reported: (r) => r.lastReportedAt ?? '',
  reported_emp: (r) => r.reportedName,
  reporter: (r) => r.reporterName,
  reports: (r) => r.reportCount,
  reason: (r) => reasonLabel(r.topReason),
  status: (r) => STATUS_META[r.status]?.label ?? r.status,
  decided: (r) => r.resolvedAt ?? '',
};

const PAGE_SIZE_OPTIONS = [
  { label: '25 / page', value: 25 },
  { label: '50 / page', value: 50 },
  { label: '100 / page', value: 100 },
];

const TH_CLS = 'whitespace-nowrap px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500';

// ═════════════════════════════════════════════════════════════════════════════

export default function SocialModerationPage() {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();

  const [rows, setRows] = useState<Array<QueueRow | PostRow>>([]);
  /** Server-side row count for All posts; the reported queue counts in memory. */
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A 403 is "you are not a moderator here", not a failure — it gets its own
  // full-page state instead of the error banner.
  const [forbidden, setForbidden] = useState(false);
  const [status, setStatus] = useState<string>('open');
  const [review, setReview] = useState<ReviewTarget | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const reqRef = useRef(0);

  // ── DM tab state, held apart from the posts state on purpose ───────────────
  // Not one `rows` array with a wider union: keeping them separate means a slow
  // posts response landing after a tab switch repopulates `rows`, which the DM
  // tab never reads, instead of overwriting the conversation list. (They still
  // share `reqRef`, so a stale response cannot flip `loading` or `error` either.)
  const [tab, setTab] = useState<ModTab>('reported');

  // Cards vs table on All posts. Defaults to the FEED, because the question this tab
  // answers most often — "what are people actually posting" — is a reading question,
  // and the table exists for the scanning one. Not persisted: a moderator who wants
  // the table flips it, and a per-session default is one fewer thing to explain.
  const [feedView, setFeedView] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  /** Who liked / commented / has seen a post, opened from a count on a feed card or a
   *  table row. ReviewModal keeps its own, stacked over itself like its AI check. */
  const [engagement, setEngagement] = useState<{ post: EngagementPanelPost; tab: EngagementTab } | null>(null);
  const openEngagement = useCallback((r: PostRow, tab: EngagementTab) => {
    setEngagement({ post: { postId: r.postId, author: r.author, content: r.content ?? r.contentPreview }, tab });
  }, []);
  const [dmRows, setDmRows] = useState<DmQueueRow[]>([]);
  const [dmReview, setDmReview] = useState<DmReviewTarget | null>(null);
  /** A DM 403 is scoped to THIS tab — the posts tabs stay usable, so it must not
   *  take the whole page down the way a posts 403 does. */
  const [dmDenied, setDmDenied] = useState<string | null>(null);
  /** Probe result. Starts false so the tab is absent while the probe is in
   *  flight; a tab that appears and then vanishes is worse than one that arrives
   *  a beat late. */
  const [dmAllowed, setDmAllowed] = useState(false);

  // ── AI review tab ─────────────────────────────────────────────────────────
  // Only the controls that live in the scaffold are held here (the pill, and the counts
  // the pills display); AiReviewTab owns its rows, fetch and modal. "Needs review" is
  // the default because it is the worklist — the other pills are the audit trail.
  const [aiFilter, setAiFilter] = useState<AiReviewFilter>('needs_review');
  const [aiCounts, setAiCounts] = useState<AiReviewCounts | null>(null);

  const {
    searchQuery, setSearchQuery,
    startDate, setStartDate,
    endDate, setEndDate,
    appliedStartDate, appliedEndDate,
    pageSize, setPageSize,
    currentPage, setCurrentPage,
    hasPendingDateChanges,
    applyDateRange,
  } = useReportFilters({ initialStartDate: daysAgoStr(30), initialEndDate: todayStr(), initialPageSize: 25 });

  const browsingAll = tab === 'all_posts';
  const browsingDm = tab === 'dm';
  const browsingAi = tab === 'ai_review';

  // ── Is this viewer allowed to read private messages here? Nobody is. ──────
  // The Direct messages tab was withdrawn as a product decision: this console
  // does not offer employees' private mail to admins. See ALL_MOD_TABS.
  //
  // What the probe used to do, for whoever restores this: `web.social_dm_moderate`
  // is a SERVER-side grant and the web app has no feature-key oracle — getUserMenus
  // carries menu codes, not `web.*` keys — so the only truthful signal was to ask.
  // One cheap probe per project rendered the tab on a 2xx, hiding it on 403 (not
  // granted) and 503 (DM schema absent). It used socialDmModerationAPI.probeQueue
  // rather than getQueue because this fired for EVERY viewer on EVERY mount, and
  // the DM routes answer an unresolvable caller with 401 — which the global
  // interceptor turns into the session-expired dialog, i.e. the plain call would
  // sign people out of the app for opening a page they are allowed to open.
  // DM PROBE — retired along with the tab. The probe existed only to decide whether
  // to render 'dm', and that entry is gone from ALL_MOD_TABS, so asking the server
  // would be a round trip per project switch whose answer nothing reads.
  //
  // Pinned false rather than deleted: `dmAllowed` / `dmDenied` still feed the
  // allowedTabs memo and the denial panel, and holding them at "not allowed" keeps
  // every one of those branches on its safe path. To restore the tab, put the probe
  // body back here and re-add the ALL_MOD_TABS entry.
  useEffect(() => {
    setDmAllowed(false);
    setDmDenied(null);
  }, [selectedProjectId]);

  // The tab is offered when the probe said yes — and ALSO while the viewer is
  // standing on it having just been refused, so the explanation below has a tab
  // to live on. Without that second clause the refusal removes the tab, the
  // snap-back bounces them to Reported posts, and the panel that says why is
  // rendered by nothing.
  const allowedTabs = useMemo(
    () => ALL_MOD_TABS.filter((t) => t.value !== 'dm' || dmAllowed || (tab === 'dm' && dmDenied != null)),
    [dmAllowed, dmDenied, tab],
  );

  // Snap the selection back into range, the same guard DashboardPage uses: a
  // viewer sitting on Direct messages when the grant is revoked (or when they
  // switch to a project that never had it) must not be left on a tab that is no
  // longer rendered, staring at a table with no way back.
  useEffect(() => {
    if (allowedTabs.length > 0 && !allowedTabs.some((t) => t.value === tab)) {
      setTab(allowedTabs[0].value);
    }
  }, [allowedTabs, tab]);

  /** Change the view and reset what the next view cannot mean, IN THE SAME BATCH.
   *  Page and search are request inputs in All posts (offset = page × size, and
   *  `q` is a server-side match), so leaving them to the post-render effect below
   *  fires one request at the old page with the old query and a second once the
   *  effect lands — the first of which is answered for an offset nobody is
   *  looking at. debouncedSearch is cleared alongside searchQuery for the same
   *  reason: it is what the fetch actually reads, and its 350 ms lag would
   *  otherwise be a third request. Sort is deliberately kept — the two posts
   *  views share one accessor map, so a sort chosen in one is still true in the
   *  other, and DM sorts on its own instance. */
  const switchTab = useCallback((next: ModTab) => {
    if (next === tab) return;
    setCurrentPage(1);
    setSearchQuery('');
    setDebouncedSearch('');
    setTab(next);
  }, [tab, setCurrentPage, setSearchQuery]);

  // Debounced only because in All posts the search is a server round-trip; the
  // reported queue still filters the fetched array on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Page, size and search are REQUEST inputs only in All posts. Pinned to
  // constants for the reported queue so flipping a client-side page there does
  // not refetch the whole queue.
  const reqPage = browsingAll ? currentPage : 1;
  const reqSize = browsingAll ? pageSize : 0;
  const reqSearch = browsingAll ? debouncedSearch : '';

  const fetchRows = useCallback(() => {
    if (!selectedProjectId) return;
    // AI review fetches for itself (AiReviewTab). Still bump the counter, so a posts
    // request in flight when the tab changed is dropped instead of landing later and
    // flipping `loading`/`error` on a view that no longer shows them.
    if (browsingAi) {
      ++reqRef.current;
      setLoading(false);
      setError(null);
      return;
    }
    // One counter for all three views. A tab switch mid-flight bumps it, so the
    // loser's response is dropped before it can touch rows, loading or error.
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    (browsingDm
      // No `status` here: the pills are hidden on this tab, so there is no value
      // to pass. The endpoint does honour all four — the queue reads each case's
      // message soft-delete state — but this view deliberately shows the whole
      // caseload for the window and narrows it in memory.
      ? socialDmModerationAPI.getQueue({
          projectId: selectedProjectId, from: appliedStartDate, to: appliedEndDate,
        })
      : browsingAll
      ? socialModerationAPI.getPosts({
          projectId: selectedProjectId, from: appliedStartDate, to: appliedEndDate,
          ...(reqSearch ? { q: reqSearch } : {}),
          limit: reqSize, offset: (reqPage - 1) * reqSize,
        })
      : socialModerationAPI.getQueue({
          projectId: selectedProjectId, status, from: appliedStartDate, to: appliedEndDate,
        })
    )
      .then((res) => {
        if (reqId !== reqRef.current) return;
        const data = (res.data?.data ?? []) as unknown[];
        if (browsingDm) {
          setDmDenied(null);
          setDmRows(data as DmQueueRow[]);
          return;
        }
        setForbidden(false);
        setRows(data as Array<QueueRow | PostRow>);
        // Absent on the DM envelope, which is why that branch returns above
        // rather than falling through and zeroing a count it does not own.
        setTotal(Number(res.data?.total ?? 0));
      })
      .catch((err: any) => {
        if (reqId !== reqRef.current) return;
        const code = err.response?.status;
        if (browsingDm) {
          setDmRows([]);
          // 403 = the message-review grant is missing, 503 = DM is not
          // provisioned. Both are permissions/availability states rather than
          // failures, and both are confined to THIS tab.
          if (code === 403 || code === 503) {
            setDmDenied(err.response?.data?.message || 'You do not have message review access for this project');
            setDmAllowed(false);
            return;
          }
          setError(err.response?.data?.message || 'Failed to load the reported conversations');
          return;
        }
        setRows([]);
        setTotal(0);
        if (code === 403) {
          setForbidden(true);
          return;
        }
        setError(err.response?.data?.message || 'Failed to load the moderation queue');
      })
      .finally(() => { if (reqId === reqRef.current) setLoading(false); });
  }, [selectedProjectId, status, appliedStartDate, appliedEndDate, browsingAll, browsingDm, browsingAi, reqPage, reqSize, reqSearch]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  // `tab` stays in the list as a BACKSTOP, not the primary path: switchTab resets
  // the page before the render that refetches, but the snap-back effect above
  // changes tabs without going through it, and page 4 of the last view is
  // meaningless in the next however the switch happened.
  useEffect(() => { setCurrentPage(1); }, [tab, status, aiFilter, appliedStartDate, appliedEndDate, searchQuery, setCurrentPage]);

  /** Same-batch reset as switchTab: AI review is server-paged, so leaving the page
   *  reset to the effect above would fire one request for the old page first. */
  const changeAiFilter = useCallback((next: AiReviewFilter) => {
    setCurrentPage(1);
    setAiFilter(next);
  }, [setCurrentPage]);

  const filtered = useMemo(() => {
    // All posts searches SERVER-side, and matches on the author's NAME, which the
    // row does not carry. Re-filtering here would drop rows the server matched on
    // purpose — and would only ever see the current page anyway.
    if (browsingAll) return rows;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return (rows as QueueRow[]).filter((r) =>
      [r.author.name, r.author.empCode, r.contentPreview, reasonLabel(r.topReason)]
        .filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [rows, searchQuery, browsingAll]);

  // Most-reported first: the queue's job is to surface what the most people are
  // complaining about, not what arrived last.
  const sort = useTableSort(filtered as QueueRow[], QUEUE_SORT_ACCESSORS, { defaultKey: 'reports', defaultDir: 'desc' });
  const clientPage = usePagination(sort.sorted, currentPage, pageSize);

  // The DM queue is capped at 500 rows and carries no `total`, so it filters,
  // sorts and pages entirely in memory — the reported-queue treatment, not the
  // All-posts one. Searching the two participants and the reason is the whole
  // vocabulary a conversation row exposes.
  const dmFiltered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return dmRows;
    return dmRows.filter((r) =>
      [r.reportedName, r.reportedEmpCode, r.reporterName, r.reporterEmpCode, reasonLabel(r.topReason)]
        .filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [dmRows, searchQuery]);

  const dmSort = useTableSort(dmFiltered, DM_SORT_ACCESSORS, { defaultKey: 'reports', defaultDir: 'desc' });
  const dmClientPage = usePagination(dmSort.sorted, currentPage, pageSize);

  // Three views, one footer. Only All posts takes its paging from the server,
  // where `total` is exact and present on every page.
  const visibleRows: Array<QueueRow | PostRow> = browsingAll ? rows : clientPage.paginatedData;
  const dmVisibleRows = dmClientPage.paginatedData;
  const totalRows = browsingDm ? dmSort.sorted.length : browsingAll ? total : sort.sorted.length;
  const totalPages = browsingDm
    ? dmClientPage.totalPages
    : browsingAll ? Math.max(1, Math.ceil(total / pageSize)) : clientPage.totalPages;
  const safePage = browsingDm
    ? dmClientPage.safePage
    : browsingAll ? Math.min(currentPage, totalPages) : clientPage.safePage;

  // Clamping the DISPLAY is not enough when the page number is also a request
  // input: a shrinking `total` would leave the fetch asking for an offset past
  // the end, and the table would sit empty while the footer read "Page 4 of 2".
  useEffect(() => {
    if (browsingAll && currentPage > totalPages) setCurrentPage(totalPages);
  }, [browsingAll, currentPage, totalPages, setCurrentPage]);

  // One shot rather than a page walk: the server caps a single request at 1000,
  // which is well past what anyone reads in a spreadsheet, and the alert below
  // says so rather than silently truncating.
  const EXPORT_LIMIT = 1000;

  const exportCsv = async () => {
    // Deliberately NO message bodies in the DM export. The queue returns none —
    // its join to the message table selects the soft-delete state and nothing
    // else, which is the whole point of that shape — and a spreadsheet of text
    // circulating on a shared drive is precisely the leak the access log exists
    // to make impossible to do quietly. The export is the case list; the content
    // stays behind the audited detail view.
    if (browsingDm) {
      const headers = ['Reported On', 'First Reported', 'Reported Employee', 'Employee Code', 'Reporter', 'Reporter Code', 'Reports', 'Open Reports', 'Top Reason', 'Status', 'Decided By', 'Decided On'];
      const data = dmSort.sorted.map((r) => [
        fmtDateTime(r.lastReportedAt),
        fmtDateTime(r.firstReportedAt),
        r.reportedName, r.reportedEmpCode,
        r.reporterName, r.reporterEmpCode,
        String(r.reportCount), String(r.openReports),
        reasonLabel(r.topReason),
        STATUS_META[r.status]?.label || r.status,
        r.resolvedBy || '-', r.resolvedAt ? fmtDate(r.resolvedAt) : '-',
      ]);
      downloadCsv(`social-dm-reports-${todayStr()}.csv`, buildCsv(headers, data));
      return;
    }
    if (!browsingAll) {
      const headers = ['Reported On', 'Type', 'Author', 'Employee Code', 'Preview', 'Media', 'Reports', 'Open Reports', 'Top Reason', 'Status', 'Decided By', 'Decided On'];
      const data = sort.sorted.map((r) => [
        fmtDateTime(r.lastReportedAt),
        r.targetType === 'post' ? 'Post' : 'Comment',
        r.author.name, r.author.empCode,
        r.contentPreview || '-',
        String(r.mediaCount), String(r.reportCount), String(r.openReports),
        reasonLabel(r.topReason),
        STATUS_META[r.status]?.label || r.status,
        r.resolvedBy || '-', r.resolvedAt ? fmtDate(r.resolvedAt) : '-',
      ]);
      downloadCsv(`social-moderation-${todayStr()}.csv`, buildCsv(headers, data));
      return;
    }
    // Only one page is in memory here, so re-request the whole filtered window
    // instead of exporting whatever happens to be on screen.
    setExporting(true);
    try {
      const res = await socialModerationAPI.getPosts({
        projectId: selectedProjectId!, from: appliedStartDate, to: appliedEndDate,
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        limit: EXPORT_LIMIT, offset: 0,
      });
      const all = (res.data?.data ?? []) as PostRow[];
      const serverTotal = Number(res.data?.total ?? 0);
      if (serverTotal > EXPORT_LIMIT) {
        alert(`Exporting the ${EXPORT_LIMIT} most recent posts of ${serverTotal}. Narrow the date range for the rest.`);
      }
      const headers = ['Posted On', 'Author', 'Employee Code', 'Content', 'Media', 'Views', 'Reports', 'Open Reports', 'Status', 'Removed By', 'Removed On', 'Removal Reason'];
      const data = all.map((r) => [
        fmtDateTime(r.createdAt),
        r.author.name, r.author.empCode,
        r.contentPreview || '-',
        String(r.mediaCount),
        r.viewCount != null ? String(r.viewCount) : '-',
        String(r.reportCount), String(r.openReports),
        STATUS_META[r.status]?.label || r.status,
        r.deletedBy || '-', r.deletedAt ? fmtDate(r.deletedAt) : '-', r.deletedReason || '-',
      ]);
      downloadCsv(`social-posts-${todayStr()}.csv`, buildCsv(headers, data));
    } catch (e: any) {
      alert(e.response?.data?.message || 'The export could not be prepared');
    } finally {
      setExporting(false);
    }
  };

  if (!selectedProjectId) {
    return <NoProjectSelectedState message="Select a project from the dropdown in the menu bar to review reported content." />;
  }

  // Name what is actually searchable in the view being looked at: All posts
  // matches the author's NAME server-side, the queue matches the report reason.
  const searchPlaceholder = browsingDm
    ? 'Search by participant, code or reason…'
    : browsingAi
    ? 'Search content, author name or employee code…'
    : browsingAll
    ? 'Search post text, author name or employee code…'
    : 'Search by author, code, content, reason…';

  // All posts is server-paged AND server-ordered (newest first), so a sortable
  // header there would reorder the 25 rows on screen and present it as a sort of
  // all 1,284. A plain header is the honest control.
  const th = (label: string, sortKey: string, align?: 'right') =>
    browsingAll
      ? <th className={`${TH_CLS}${align === 'right' ? ' text-right' : ''}`}>{label}</th>
      : <SortTh label={label} sortKey={sortKey} sort={sort} className={TH_CLS} align={align} />;

  if (forbidden) {
    return (
      <AccessDeniedPanel
        message="You don't have moderation access for this project. Social Moderation needs the Social Moderation page ticked for your role and project-wide data visibility — ask an administrator in Access Management."
        onLogout={() => { clearAuth(); navigate('/login'); }}
      />
    );
  }

  return (
    <ReportPageScaffold
      title="Social Moderation"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder={searchPlaceholder}
      // The VIEW switcher, in the Dashboard's markup so the two pages read as one
      // product: a recessed gray tray, the active view a raised white chip.
      tabs={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
            {allowedTabs.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => switchTab(t.value)}
                className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  tab === t.value ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Says out loud what this tab is and is not, so nobody goes looking
              for the browse-all control that exists one tab to the left. */}
          {browsingDm && (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <Eye className="h-3.5 w-3.5" />
              Reported conversations only · every conversation you open is logged
            </span>
          )}
        </div>
      }
      filters={
        <>
          {/* AI review's own filter, carrying the range-wide count of each bucket. */}
          {browsingAi && (
            <AiReviewFilterPills value={aiFilter} onChange={changeAiFilter} counts={aiCounts} />
          )}

          {/* A FILTER on the reported worklist, so it stays in the filters row —
              and is absent on the other tabs, where none of its five values
              describes anything the view can contain. */}
          {tab === 'reported' && (
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setStatus(t.value)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    status === t.value ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            hasPendingDateChanges={hasPendingDateChanges}
            onStartDateChange={(e) => setStartDate(e.target.value)}
            onEndDateChange={(e) => setEndDate(e.target.value)}
            onStartDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onEndDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onApply={applyDateRange}
          />

          {/* Feed / Table, on All posts only. Reported posts stays a worklist — a
              queue is scanned and actioned, and cards make that slower, not nicer. */}
          {browsingAll && (
            <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
              <button
                type="button"
                onClick={() => setFeedView(true)}
                className={`px-3 py-2 text-sm font-medium ${feedView ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >Feed</button>
              <button
                type="button"
                onClick={() => setFeedView(false)}
                className={`border-l border-gray-300 px-3 py-2 text-sm font-medium ${!feedView ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >Table</button>
            </div>
          )}

          {/* Not on AI review: its rows include blocked attempts — the customer
              numbers and payslips the policy exists to keep off the feed — and a
              spreadsheet of them is the leak the block prevented. */}
          {!browsingAi && (
            <button
              type="button"
              onClick={exportCsv}
              disabled={totalRows === 0 || exporting}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> {exporting ? 'Preparing…' : 'Download'}
            </button>
          )}

          {/* Composing is offered on All posts only: the reported queue is about
              judging what exists, not adding to it. */}
          {browsingAll && selectedProjectId && (
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700"
            >
              <Plus className="h-4 w-4" /> New post
            </button>
          )}
        </>
      }
    >
      {error && !browsingAi && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {browsingAi ? (
        <AiReviewTab
          projectId={selectedProjectId}
          filter={aiFilter}
          from={appliedStartDate}
          to={appliedEndDate}
          search={debouncedSearch}
          page={currentPage}
          pageSize={pageSize}
          setPage={setCurrentPage}
          setPageSize={setPageSize}
          onCounts={setAiCounts}
          // Same moderator gate as the other tabs, so the same full-page panel.
          onForbidden={() => setForbidden(true)}
        />
      ) : loading ? (
        <BlockLoader className="h-60" />
      ) : browsingDm && dmDenied ? (
        // Scoped to the tab, NOT the page: the two posts tabs are a different
        // grant and are still working, so replacing the whole screen the way a
        // posts 403 does would log the viewer out of work they can still do.
        // In practice this only appears if the grant is pulled mid-session, which
        // is also why allowedTabs keeps 'dm' present while dmDenied is set: the
        // refusal must be readable on the tab that was refused, not swallowed by
        // a silent bounce back to Reported posts. Switching away or changing
        // project retires the tab for good.
        <div className="mt-4 rounded-lg border border-dashed border-gray-200 px-6 py-12 text-center">
          <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <div className="text-sm font-medium text-gray-700">{dmDenied}</div>
          <p className="mx-auto mt-1 max-w-md text-xs text-gray-500">
            Reviewing private messages needs the Social Moderation page ticked for your role in this
            project, plus project-wide data visibility.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-2 mt-3 text-xs text-gray-500">
            {browsingDm
              ? `${totalRows} reported conversation${totalRows === 1 ? '' : 's'}${
                  searchQuery.trim() && totalRows !== dmRows.length ? ` of ${dmRows.length}` : ''}`
              : browsingAll
              ? `${total} post${total === 1 ? '' : 's'}${debouncedSearch ? ' matching your search' : ''}`
              : `${totalRows} reported item${totalRows === 1 ? '' : 's'}${
                  searchQuery.trim() && totalRows !== rows.length ? ` of ${rows.length}` : ''}`}
          </div>
          {browsingAll && feedView ? (
            // The same rows the table renders, as cards. No second fetch: the list
            // endpoint now returns full text, media and audience alongside the
            // columns the table uses, so the two views are the same data drawn twice
            // and can never disagree about what exists.
            visibleRows.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 px-6 py-12 text-center text-sm text-gray-500">
                No posts match these filters. Try widening the date range or clearing the search.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {(visibleRows as PostRow[]).map((r) => (
                  <FeedPostCard
                    key={r.postId}
                    post={r as unknown as FeedPost}
                    projectId={selectedProjectId as number}
                    onReview={() => setReview({ targetType: 'post', targetId: r.targetId, author: r.author, status: r.status })}
                    onEngagement={(_, tab) => openEngagement(r, tab)}
                  />
                ))}
              </div>
            )
          ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-100">
            {browsingDm ? (
              <DmQueueTable rows={dmVisibleRows} sort={dmSort} onReview={setDmReview} />
            ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50/70">
                <tr>
                  {th(browsingAll ? 'Posted' : 'Reported', 'reported')}
                  {/* Type would be the constant "Post", and there is no report
                      cluster to name a top reason from. */}
                  {!browsingAll && th('Type', 'type')}
                  {th('Author', 'author')}
                  {th(browsingAll ? 'Content' : 'Preview', 'preview')}
                  {th('Media', 'media', 'right')}
                  {th('Reports', 'reports', 'right')}
                  {!browsingAll && th('Top reason', 'reason')}
                  {/* All posts only: the queue's rows are report clusters, and a comment
                      row has no likes or views of its own. Not sortable — the view is
                      server-ordered, see th() above. */}
                  {browsingAll && <th className={TH_CLS}>Engagement</th>}
                  {th('Status', 'status')}
                  {th(browsingAll ? 'Removed' : 'Decided', 'decided')}
                  <th className={`${TH_CLS} text-center`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {visibleRows.length === 0 ? (
                  <tr><td colSpan={browsingAll ? 9 : 10} className="py-12 text-center text-sm text-gray-400">
                    {browsingAll ? (
                      // A moderator hunting a three-month-old post inside the
                      // default 30-day window must not read this as "it never
                      // existed".
                      'No posts match these filters. Try widening the date range or clearing the search.'
                    ) : (
                      // An empty queue is the healthy state, but it is also the
                      // state that makes the page look broken — so it names the
                      // way out instead of dead-ending.
                      <>
                        No reported content.
                        <button
                          type="button"
                          onClick={() => switchTab('all_posts')}
                          className="ml-1 font-medium text-primary-600 hover:underline"
                        >
                          Browse all posts instead
                        </button>
                      </>
                    )}
                  </td></tr>
                ) : visibleRows.map((r) => (
                  <tr
                    key={`${r.targetType}:${r.targetId}`}
                    onClick={() => setReview(r)}
                    className="cursor-pointer align-top hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                      {fmtDate(browsingAll ? (r as PostRow).createdAt : (r as QueueRow).lastReportedAt)}
                    </td>
                    {!browsingAll && (
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          r.targetType === 'post' ? 'bg-indigo-50 text-indigo-700' : 'bg-sky-50 text-sky-700'
                        }`}>
                          {r.targetType === 'post' ? 'Post' : 'Comment'}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.author.name}</div>
                      <div className="font-mono text-xs text-gray-400">{r.author.empCode}</div>
                    </td>
                    <td className="max-w-[320px] px-4 py-3 text-gray-700">
                      {/* Struck through when it is no longer on the feed — the one
                          place the row's own text can say "this is gone". */}
                      <div
                        className={`truncate ${r.status === 'removed' || r.status === 'author_deleted' ? 'text-gray-400 line-through' : ''}`}
                        title={r.contentPreview}
                      >
                        {r.contentPreview || <span className="text-gray-400">—</span>}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-gray-600">
                      {r.mediaCount > 0 ? (
                        <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> ×{r.mediaCount}</span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* A no-op for the queue, whose rows are always ≥ 1 report.
                          It stops All posts painting a rose "0" on every healthy
                          row and turning the exception colour into the norm. */}
                      {r.reportCount > 0 ? (
                        <>
                          <span className="inline-flex min-w-[2rem] justify-center rounded-full bg-rose-50 px-2 py-0.5 text-sm font-semibold text-rose-600">
                            {r.reportCount}
                          </span>
                          {r.openReports > 0 && r.openReports !== r.reportCount && (
                            <div className="mt-0.5 text-[11px] text-gray-400">{r.openReports} open</div>
                          )}
                        </>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    {!browsingAll && (
                      <td className="px-4 py-3 text-gray-700">{reasonLabel((r as QueueRow).topReason)}</td>
                    )}
                    {browsingAll && (
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                        {/* Each number opens who; EngagementCounts stops the click
                            reaching the row, which would open the review modal. */}
                        <EngagementCounts
                          likes={r.likeCount}
                          comments={r.commentCount}
                          views={(r as PostRow).viewCount}
                          onOpen={(tab) => openEngagement(r as PostRow, tab)}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {/* `live` carries no background, so a healthy post reads as
                          plain grey text and only the removals get a chip. */}
                      <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[r.status]?.cls || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_META[r.status]?.label || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {browsingAll ? (
                        (r as PostRow).deletedAt ? (
                          <>
                            <div className="text-xs font-medium text-gray-700" title={r.deletedReason ?? undefined}>
                              {(r as PostRow).deletedBy || '—'}
                            </div>
                            <div className="text-xs text-gray-400">{fmtDate((r as PostRow).deletedAt)}</div>
                          </>
                        ) : <span className="text-gray-300">—</span>
                      ) : (r as QueueRow).resolvedAt ? (
                        <>
                          <div className="text-xs font-medium text-gray-700">{(r as QueueRow).resolvedBy || '—'}</div>
                          <div className="text-xs text-gray-400">{fmtDate((r as QueueRow).resolvedAt)}</div>
                        </>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center"><TableActionGroup>
                      <TableActionButton action="review" label="Review" type="button" onClick={(e) => { e.stopPropagation(); setReview(r); }} />
                    </TableActionGroup></td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
          )}

          {/* One footer, two sources of truth: an exact server `total` in All
              posts, the in-memory row count in the queue. onPageSizeChange
              resetting to page 1 is load-bearing in the former, not cosmetic —
              the request offset is derived from page × size. */}
          {totalRows > 0 && (
            <PaginationFooter
              safePage={safePage}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              totalRows={totalRows}
              onPageSizeChange={(v) => { setPageSize(v); setCurrentPage(1); }}
              onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
              onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            />
          )}
        </>
      )}

      {review && createPortal(
        <ReviewModal
          row={review}
          projectId={selectedProjectId}
          onClose={() => setReview(null)}
          onChanged={fetchRows}
        />,
        document.body,
      )}

      {dmReview && createPortal(
        <DmReviewModal
          // Keyed on the conversation so opening a different case starts from its
          // own anchor rather than inheriting the last one's re-centred window.
          key={dmReview.conversationId}
          row={dmReview}
          projectId={selectedProjectId}
          onClose={() => setDmReview(null)}
          onChanged={fetchRows}
        />,
        document.body,
      )}

      {engagement && createPortal(
        <EngagementPanel
          // Keyed on the post so a different post never inherits the last one's lists.
          key={engagement.post.postId}
          projectId={selectedProjectId}
          post={engagement.post}
          initialTab={engagement.tab}
          onClose={() => setEngagement(null)}
        />,
        document.body,
      )}

      {/* Portalled like the review modals, for the same reason: the scaffold clips
          overflow, so a modal rendered inside it scrolls with the table. */}
      {composerOpen && selectedProjectId && createPortal(
        <PostComposerModal
          projectId={selectedProjectId}
          onClose={() => setComposerOpen(false)}
          // A new post lands at the top of the default date window, so refetching is
          // enough — no optimistic insert to reconcile against the server's ordering.
          onPublished={fetchRows}
        />,
        document.body,
      )}
    </ReportPageScaffold>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Direct messages — the reported-conversation worklist.

/** Its own table rather than more `!browsingDm &&` guards on the posts one: the
 *  two share not one column. A DM row names two PEOPLE and no content — the
 *  message bodies deliberately do not appear in a scannable list, because a
 *  private message legible to anyone walking past the reviewer's monitor is the
 *  disclosure quietly breaking its promise. The text lives one click away, in
 *  the detail view, where opening it writes an audit row. */
function DmQueueTable({ rows, sort, onReview }: {
  rows: DmQueueRow[];
  sort: ReturnType<typeof useTableSort<DmQueueRow>>;
  onReview: (row: DmQueueRow) => void;
}) {
  return (
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <thead className="bg-gray-50/70">
        <tr>
          <SortTh label="Reported" sortKey="reported" sort={sort} className={TH_CLS} />
          <SortTh label="Reported employee" sortKey="reported_emp" sort={sort} className={TH_CLS} />
          <SortTh label="Reported by" sortKey="reporter" sort={sort} className={TH_CLS} />
          <SortTh label="Reports" sortKey="reports" sort={sort} className={TH_CLS} align="right" />
          <SortTh label="Top reason" sortKey="reason" sort={sort} className={TH_CLS} />
          <SortTh label="Status" sortKey="status" sort={sort} className={TH_CLS} />
          <SortTh label="Decided" sortKey="decided" sort={sort} className={TH_CLS} />
          <th className={`${TH_CLS} text-center`}>Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {rows.length === 0 ? (
          <tr><td colSpan={8} className="py-12 text-center text-sm text-gray-400">
            {/* No "browse all conversations instead" escape hatch here, unlike
                the posts queue. There is no such view, by design. */}
            No reported conversations in this date range.
          </td></tr>
        ) : rows.map((r) => (
          <tr
            key={r.conversationId}
            onClick={() => onReview(r)}
            className="cursor-pointer align-top hover:bg-gray-50"
          >
            <td className="whitespace-nowrap px-4 py-3 text-gray-600">
              <div>{fmtDate(r.lastReportedAt)}</div>
              {/* A campaign spread over weeks reads very differently from a
                  single bad evening, and only the two dates together say which. */}
              {r.reportCount > 1 && r.firstReportedAt !== r.lastReportedAt && (
                <div className="text-[11px] text-gray-400">since {fmtDate(r.firstReportedAt)}</div>
              )}
            </td>
            <td className="px-4 py-3">
              <div className="font-medium text-gray-900">{r.reportedName}</div>
              <div className="font-mono text-xs text-gray-400">{r.reportedEmpCode}</div>
            </td>
            <td className="px-4 py-3">
              <div className="text-gray-700">{r.reporterName}</div>
              <div className="font-mono text-xs text-gray-400">{r.reporterEmpCode}</div>
            </td>
            <td className="px-4 py-3 text-right">
              <span className="inline-flex min-w-[2rem] justify-center rounded-full bg-rose-50 px-2 py-0.5 text-sm font-semibold text-rose-600">
                {r.reportCount}
              </span>
              {r.openReports > 0 && r.openReports !== r.reportCount && (
                <div className="mt-0.5 text-[11px] text-gray-400">{r.openReports} open</div>
              )}
            </td>
            <td className="px-4 py-3 text-gray-700">{reasonLabel(r.topReason)}</td>
            <td className="px-4 py-3">
              <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[r.status]?.cls || 'bg-gray-100 text-gray-600'}`}>
                {STATUS_META[r.status]?.label || r.status}
              </span>
              {/* Only where it says something the chip does not: a case still
                  open that has already had a message pulled, or more than the
                  one "Content removed" already implies. */}
              {r.removedMessages > 0 && (r.status !== 'removed' || r.removedMessages > 1) && (
                <div className="mt-0.5 text-[11px] text-gray-400">
                  {r.removedMessages} message{r.removedMessages === 1 ? '' : 's'} removed
                </div>
              )}
            </td>
            <td className="px-4 py-3 text-gray-600">
              {r.resolvedAt ? (
                <>
                  <div className="text-xs font-medium text-gray-700">{r.resolvedBy || '—'}</div>
                  <div className="text-xs text-gray-400">{fmtDate(r.resolvedAt)}</div>
                </>
              ) : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-4 py-3 text-center"><TableActionGroup>
              <TableActionButton action="review" label="Review" type="button" onClick={(e) => { e.stopPropagation(); onReview(r); }} />
            </TableActionGroup></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Review modal — the content, every reporter, the thread, and the decision.

type PendingRemoval = { targetType: TargetType; targetId: string; label: string; who: string };

function ReviewModal({ row, projectId, onClose, onChanged }: {
  // ReviewTarget, not QueueRow: a row from All posts has no report cluster, and
  // the modal reads every fact that matters off its own fresh getItem load.
  row: ReviewTarget;
  projectId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [item, setItem] = useState<ModItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmRemoval, setConfirmRemoval] = useState<PendingRemoval | null>(null);
  const [lightbox, setLightbox] = useState<{ path: string; alt: string } | null>(null);
  /** The AI check opened on top of this modal, by review id. */
  const [aiOpen, setAiOpen] = useState<string | number | null>(null);
  /** Who liked / commented / has seen the post, opened on top of this modal. */
  const [engagementTab, setEngagementTab] = useState<EngagementTab | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    socialModerationAPI
      .getItem(row.targetType, row.targetId, projectId)
      .then((res) => setItem((res.data?.data ?? null) as ModItem | null))
      .catch((e: any) => setErr(e.response?.data?.message || 'Failed to load the reported item'))
      .finally(() => setLoading(false));
  }, [row.targetType, row.targetId, projectId]);
  useEffect(() => { load(); }, [load]);

  const post = item?.post ?? null;
  const targetComment = useMemo(
    () => item?.comments.find((c) => c.isTarget) ?? null,
    [item],
  );
  // Deleted state of the TARGET is read from the freshly loaded item, never from
  // the queue row — the row is a snapshot from before this modal's own actions.
  const targetDeletedKind = row.targetType === 'post' ? post?.deletedKind ?? null : targetComment?.deletedKind ?? null;
  const targetIsLive = row.targetType === 'post' ? !post?.deletedAt : !targetComment?.deletedAt;
  // Held / rejected under the Connect content policy. Defaulted, because an older API
  // sends no reviewState and everything it returns is published.
  const targetReviewState: ReviewState =
    (row.targetType === 'post' ? post?.reviewState : targetComment?.reviewState) ?? 'published';
  /** On other people's feeds right now. A takedown is only for this: a held post is
   *  approved or rejected from AI review instead, because a moderation takedown counts
   *  toward the author's red-flag metrics and a held post has harmed nobody yet. */
  const targetIsPublished = targetIsLive && targetReviewState === 'published';
  const ai = item?.aiReview ?? null;
  // getItem's summary row may not carry the mode. A PUBLISHED item the AI would have
  // held or blocked can only have come through monitor mode — enforce would have held
  // or refused it — so the Monitor tag is inferred rather than silently dropped.
  const aiMode = ai
    ? ai.mode ?? (ai.outcome === 'published' && (ai.decision === 'hold' || ai.decision === 'block') ? 'monitor' : 'enforce')
    : null;
  const openReports = useMemo(
    () => (item?.reports ?? []).filter((r) => r.status === 'open').length,
    [item],
  );
  // Same derivation the server does for the queue, recomputed here so the header
  // pill tracks THIS modal's own actions. Reading row.status would leave it saying
  // "Pending review" directly above a red "Removed by …" banner the moment a
  // moderator acts, because the row is a snapshot from before the action.
  // Falls back to the row while the detail is still loading.
  const liveStatus: ModStatus = useMemo(() => {
    if (!item) return row.status;
    if (!targetIsLive) return targetDeletedKind === 'moderation' ? 'removed' : 'author_deleted';
    if (targetReviewState === 'held') return 'held';
    if (targetReviewState === 'rejected') return 'not_approved';
    if (openReports > 0) return 'open';
    // Nothing was ever reported: this was opened from All posts, not adjudicated.
    // "No action" here would claim a decision nobody made.
    return item.reports.length === 0 ? 'live' : 'dismissed';
  }, [item, row.status, targetIsLive, targetDeletedKind, targetReviewState, openReports]);

  const trimmedReason = reason.trim();

  const act = async (key: string, run: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await run();
      setReason('');
      load();
      onChanged();
    } catch (e: any) {
      alert(e.response?.data?.message || 'The action could not be completed');
    } finally {
      setBusy(null);
    }
  };

  const doDismiss = () =>
    act('dismiss', () => socialModerationAPI.dismiss(row.targetType, row.targetId, projectId, trimmedReason || undefined));

  const doTakedown = (target: PendingRemoval) =>
    act('takedown', () => socialModerationAPI.takedown(target.targetType, target.targetId, projectId, trimmedReason));

  const doRestore = (targetType: TargetType, targetId: string) =>
    act('restore', () => socialModerationAPI.restore(targetType, targetId, projectId, trimmedReason || undefined));

  return (
    // Close only on a DIRECT backdrop click. Without the target===currentTarget
    // guard, a click inside the image lightbox bubbles up through the React tree
    // to this handler and closes the whole review modal with it.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 p-5">
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              {row.author.name}
              <span className="font-mono text-xs font-normal text-gray-400">{row.author.empCode}</span>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[liveStatus]?.cls || 'bg-gray-100 text-gray-600'}`}>
                {STATUS_META[liveStatus]?.label || liveStatus}
              </span>
            </div>
            <div className="mt-0.5 text-sm text-gray-500">
              {/* Don't say "Reported" about something nobody reported — All posts
                  opens live, never-reported content through this same modal. */}
              {item && item.reports.length === 0
                ? (row.targetType === 'post' ? 'Post' : 'Comment')
                : `Reported ${row.targetType === 'post' ? 'post' : 'comment'}`} ·{' '}
              {[row.author.designation, row.author.storeName].filter(Boolean).join(' · ') || 'No store on record'}
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <BlockLoader className="h-48" />
        ) : err ? (
          <div className="p-6 text-center text-sm text-red-600">{err}</div>
        ) : post ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <div className="grid gap-5 lg:grid-cols-[3fr,2fr]">
              {/* Left — the content itself + the thread */}
              <div className="space-y-4">
                <section className={`rounded-lg border p-4 ${post.deletedAt ? 'border-red-100 bg-red-50/40' : 'border-gray-100 bg-gray-50/60'}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Post by {post.author.name}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {post.postType === 'announcement' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-medium text-purple-700">
                          <Pin className="h-3 w-3" /> Announcement
                        </span>
                      )}
                      {post.isPinned && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">Pinned</span>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{post.content || <span className="text-gray-400">No text</span>}</p>

                  {post.media.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {post.media.map((m) => (
                        <AuthedImage
                          key={m.id}
                          path={m.thumbnailPath || m.path}
                          alt="Reported photo"
                          className="h-24 w-24 cursor-pointer rounded-md object-cover"
                          onClick={() => setLightbox({ path: m.path, alt: 'Reported photo' })}
                        />
                      ))}
                    </div>
                  )}

                  {/* Blast radius — how many people already engaged with this, and
                      how long it has been live. The deciding context for whether a
                      removal is still worth making. */}
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                    {/* The POST's engagement, also when the row under review is one of
                        its comments. Each count opens who. */}
                    <EngagementCounts
                      labelled
                      likes={post.likeCount}
                      comments={post.commentCount}
                      views={post.viewCount}
                      onOpen={setEngagementTab}
                    />
                    <span>Posted {fmtDateTime(post.createdAt)}</span>
                    <span className="text-gray-400">up for {fmtAge(post.createdAt)}</span>
                  </div>

                  {post.deletedAt && (
                    <div className="mt-3 rounded-md bg-white/70 p-2 text-[11px] text-red-700">
                      {post.deletedKind === 'moderation'
                        ? <>Removed by {post.deletedBy || 'a moderator'} on {fmtDateTime(post.deletedAt)}{post.deletedReason ? ` — “${post.deletedReason}”` : ''}</>
                        : <>Deleted by the author on {fmtDateTime(post.deletedAt)}</>}
                    </div>
                  )}
                  {post.restoredAt && (
                    <div className="mt-1.5 text-[11px] text-green-700">
                      Restored by {post.restoredBy || 'a moderator'} on {fmtDateTime(post.restoredAt)}
                    </div>
                  )}
                  {!post.deletedAt && post.reviewState === 'held' && (
                    <div className="mt-3 rounded-md bg-amber-50 p-2 text-[11px] text-amber-800">
                      Held for review — only the author can see this post until a moderator approves it.
                      {post.reviewNote ? <> The author sees: “{post.reviewNote}”</> : null}
                    </div>
                  )}
                  {!post.deletedAt && post.reviewState === 'rejected' && (
                    <div className="mt-3 rounded-md bg-rose-50 p-2 text-[11px] text-rose-700">
                      Not approved — never shown to anyone but the author.
                      {post.reviewNote ? <> Reason given: “{post.reviewNote}”</> : null}
                    </div>
                  )}
                </section>

                <section>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Comments ({item?.comments.length ?? 0})
                  </div>
                  {(item?.comments.length ?? 0) === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-xs text-gray-400">No comments.</div>
                  ) : (
                    <div className="space-y-2">
                      {item!.comments.map((c) => (
                        <CommentRow
                          key={c.id}
                          comment={c}
                          busy={busy != null}
                          reasonReady={!!trimmedReason}
                          onRemove={() => setConfirmRemoval({
                            targetType: 'comment', targetId: c.id,
                            label: 'comment by', who: c.author.name,
                          })}
                          onRestore={() => doRestore('comment', c.id)}
                        />
                      ))}
                    </div>
                  )}
                </section>
              </div>

              {/* Right — the AI's verdict (when there was a check), then every reporter */}
              <div>
                {ai && (
                  <div className="mb-4">
                    <AiVerdictPanel
                      decision={ai.decision}
                      mode={aiMode}
                      summary={ai.summary}
                      violations={ai.violations ?? []}
                      sentence={outcomeSentence({
                        decision: ai.decision,
                        outcome: ai.outcome,
                        mode: aiMode ?? 'enforce',
                        targetType: ai.targetType ?? row.targetType,
                      })}
                    >
                      <button
                        type="button"
                        onClick={() => setAiOpen(ai.id)}
                        className="mt-2 text-xs font-medium text-primary-600 hover:underline"
                      >
                        {ai.moderatorStatus === 'pending' ? 'Decide in AI review →' : 'Open the AI check →'}
                      </button>
                    </AiVerdictPanel>
                  </div>
                )}
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Reports ({item?.reports.length ?? 0}{openReports ? ` · ${openReports} open` : ''})
                </div>
                {(item?.reports.length ?? 0) === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-xs text-gray-400">No reports on record.</div>
                ) : (
                  <div className="space-y-2">
                    {item!.reports.map((rep) => (
                      <div key={rep.id} className="rounded-lg border border-gray-100 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-800">{rep.reporter.name}</div>
                            <div className="font-mono text-[11px] text-gray-400">{rep.reporter.empCode}</div>
                          </div>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            rep.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {rep.status === 'open' ? 'Open' : 'Actioned'}
                          </span>
                        </div>
                        <div className="mt-1.5 text-sm text-gray-700">{reasonLabel(rep.reasonCode)}</div>
                        {rep.reasonText && <p className="mt-0.5 whitespace-pre-wrap text-xs text-gray-600">“{rep.reasonText}”</p>}
                        <div className="mt-1.5 text-[11px] text-gray-400">{fmtDateTime(rep.createdAt)}</div>
                        {rep.resolution && (
                          <div className="mt-1.5 border-t border-gray-100 pt-1.5 text-[11px] text-gray-500">
                            {rep.resolution === 'takedown' ? 'Content removed' : 'No action taken'}
                            {rep.resolvedBy ? ` by ${rep.resolvedBy}` : ''}
                            {rep.resolvedAt ? ` · ${fmtDate(rep.resolvedAt)}` : ''}
                            {rep.resolutionNote ? <div className="mt-0.5 text-gray-600">“{rep.resolutionNote}”</div> : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Footer — the mandatory reason + the decision */}
        {post && (
          <div className="shrink-0 border-t border-gray-100 p-4">
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Removal reason <span className="text-red-600">*</span>
              <span className="ml-1 font-normal text-gray-400">
                required to remove anything — it is the record shown if the employee escalates. Removals are reversible from this queue.
              </span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Why is this being removed? (also attached as the note on a dismissal)"
              className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={doDismiss}
                disabled={busy != null || openReports === 0}
                title={openReports === 0 ? 'No open reports left to dismiss' : 'Close every open report with no action'}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
              </button>
              {targetDeletedKind === 'moderation' && (
                <button
                  type="button"
                  onClick={() => doRestore(row.targetType, row.targetId)}
                  disabled={busy != null}
                  className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  <Undo2 className="h-4 w-4" /> {busy === 'restore' ? 'Restoring…' : 'Restore'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setConfirmRemoval({
                  targetType: row.targetType, targetId: row.targetId,
                  label: row.targetType === 'post' ? 'post by' : 'comment by', who: row.author.name,
                })}
                disabled={busy != null || !trimmedReason || !targetIsPublished}
                title={
                  !targetIsLive ? 'This content is already removed'
                    : targetReviewState === 'held' ? 'Held content is approved or rejected from AI review, not taken down'
                    : targetReviewState === 'rejected' ? 'This was not approved, so it is not on anyone’s feed'
                    : !trimmedReason ? 'Enter a removal reason first'
                    : 'Remove this content from the feed'
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                <ShieldOff className="h-4 w-4" /> {busy === 'takedown' ? 'Removing…' : 'Take down'}
              </button>
            </div>
          </div>
        )}
      </div>

      {lightbox && <AuthedLightbox path={lightbox.path} alt={lightbox.alt} onClose={() => setLightbox(null)} />}

      {/* Stacked over this modal rather than replacing it: the moderator came here
          from a post and should land back on it, now showing the decision. */}
      {aiOpen != null && createPortal(
        <AiReviewModal
          reviewId={aiOpen}
          projectId={projectId}
          onClose={() => setAiOpen(null)}
          onChanged={() => { load(); onChanged(); }}
        />,
        document.body,
      )}

      {/* Stacked over this modal the same way; closing it lands back on the post. */}
      {engagementTab && post && createPortal(
        <EngagementPanel
          projectId={projectId}
          post={{ postId: post.id, author: post.author, content: post.content }}
          initialTab={engagementTab}
          onClose={() => setEngagementTab(null)}
        />,
        document.body,
      )}

      <ConfirmDeleteModal
        isOpen={confirmRemoval != null}
        onClose={() => setConfirmRemoval(null)}
        onConfirm={() => {
          const target = confirmRemoval;
          setConfirmRemoval(null);
          if (target) doTakedown(target);
        }}
        isSubmitting={busy === 'takedown'}
        title="Remove content"
        entityLabel={confirmRemoval?.label ?? 'content by'}
        entityName={confirmRemoval?.who ?? ''}
        // A takedown is a soft-delete: the row, its media and the full report
        // history are kept, and Restore in this same modal reverses it. The
        // default "cannot be undone" copy would flatly contradict the reversibility
        // note sitting a few lines below it on the screen behind this dialog.
        consequenceNote="It disappears from the feed immediately. You can restore it from this queue."
        confirmLabel="Remove"
      />
    </div>
  );
}

// ── One comment in the thread ────────────────────────────────────────────────
// Rendered for the whole thread, deleted comments included: a moderator judging a
// reported comment needs the conversation around it, and needs to see what has
// already been actioned so they don't re-litigate it.
function CommentRow({ comment, busy, reasonReady, onRemove, onRestore }: {
  comment: ModComment;
  busy: boolean;
  reasonReady: boolean;
  onRemove: () => void;
  onRestore: () => void;
}) {
  const removed = comment.deletedAt != null;
  // A comment the AI held is visible only to its author. It is approved or rejected
  // from AI review; taking it down here would count as a moderation removal against
  // an author whose comment nobody else has seen.
  const reviewState = comment.reviewState ?? 'published';
  return (
    <div className={`rounded-lg p-2.5 text-sm ${
      comment.isTarget ? 'bg-amber-50 ring-1 ring-amber-200' : removed ? 'bg-gray-50 opacity-70' : 'bg-gray-100'
    }`}>
      <div className="mb-0.5 flex items-center justify-between gap-2 text-[11px] text-gray-500">
        <span className="truncate font-medium text-gray-700">
          {comment.author.name}
          {comment.isTarget && <span className="ml-1.5 rounded bg-amber-200 px-1 text-[10px] font-semibold text-amber-800">REPORTED</span>}
          {comment.reportCount > 0 && <span className="ml-1.5 text-rose-600">{comment.reportCount} report{comment.reportCount === 1 ? '' : 's'}</span>}
          {!removed && reviewState === 'held' && <span className="ml-1.5 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700">HELD FOR REVIEW</span>}
          {!removed && reviewState === 'rejected' && <span className="ml-1.5 rounded bg-rose-50 px-1 text-[10px] font-semibold text-rose-700">NOT APPROVED</span>}
        </span>
        <span className="shrink-0">{fmtDateTime(comment.createdAt)}</span>
      </div>
      <p className="whitespace-pre-wrap text-gray-800">{comment.content}</p>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="text-[11px] text-red-600">
          {removed && (comment.deletedKind === 'moderation'
            ? `Removed${comment.deletedReason ? ` — “${comment.deletedReason}”` : ''}`
            : 'Deleted by the author')}
        </span>
        {/* Per-comment actions so a single bad comment can be removed without
            taking down the post it sits under. */}
        {!removed && reviewState !== 'published' ? (
          <span className="shrink-0 text-[11px] text-gray-400">
            {reviewState === 'held' ? 'Decide in AI review' : 'Not on the feed'}
          </span>
        ) : !removed ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={busy || !reasonReady}
            title={reasonReady ? 'Remove this comment' : 'Enter a removal reason below first'}
            className="shrink-0 rounded border border-red-200 px-2 py-0.5 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
          >
            Remove
          </button>
        ) : comment.deletedKind === 'moderation' ? (
          <button
            type="button"
            onClick={onRestore}
            disabled={busy}
            className="shrink-0 rounded border border-green-200 px-2 py-0.5 text-[11px] font-medium text-green-700 hover:bg-green-50 disabled:opacity-40"
          >
            Restore
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// DM review modal — the bounded window, the reporters, and the decision.
//
// A separate component from ReviewModal, not a widened one. Everything differs:
// the row is a conversation rather than a (targetType, targetId) pair; the reads
// are audited; there is no media, no comment thread and no restore; and both
// writes key on a MESSAGE while the row that opened this is a conversation.
//
// PRIVACY IS THE SHAPE OF THIS SCREEN. It renders exactly what getDmItem
// returns — a ±25-message window around the reported message — and does not ask
// for more. There is no "load earlier", no infinite scroll and no thread export:
// a report about one message is not consent to read a year of somebody's mail,
// and the widening would be a client-side decision to override a server-side
// privacy bound.

function DmReviewModal({ row, projectId, onClose, onChanged }: {
  row: DmReviewTarget;
  projectId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [item, setItem] = useState<DmItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmRemoval, setConfirmRemoval] = useState<{ messageId: string; who: string } | null>(null);

  /** The message the window is centred on — the takedown/dismiss handle and the
   *  anchor the server measures its ±DM_MOD_WINDOW from. Seeded from the newest
   *  report, but RE-ANCHORABLE, because a case whose reports span more than that
   *  window has reports the window cannot reach. Only ever moved to a message
   *  that is itself REPORTED in this case (see the Reports panel): re-centring on
   *  a reported message is inside the consent the report gave, whereas a free
   *  cursor would be the client quietly widening a server-side privacy bound. */
  const [anchorId, setAnchorId] = useState(row.latestMessageId);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    socialDmModerationAPI
      .getItem({ projectId, conversationId: row.conversationId, messageId: anchorId })
      .then((res) => setItem((res.data?.data ?? null) as DmItem | null))
      .catch((e: any) => setErr(e.response?.data?.message || 'Failed to load the conversation'))
      .finally(() => setLoading(false));
  }, [projectId, row.conversationId, anchorId]);
  useEffect(() => { load(); }, [load]);

  // Open reports per MESSAGE, because that is the granularity both writes work
  // at. A conversation whose reports span three messages needs three decisions,
  // and pretending otherwise would leave the queue row stubbornly "open" after a
  // reviewer believed they had cleared it.
  const openByMessage = useMemo(() => {
    const m = new Map<string, number>();
    for (const rep of item?.reports ?? []) {
      if (rep.status === 'open') m.set(rep.messageId, (m.get(rep.messageId) ?? 0) + 1);
    }
    return m;
  }, [item]);
  const totalOpen = useMemo(
    () => Array.from(openByMessage.values()).reduce((a, b) => a + b, 0),
    [openByMessage],
  );
  const anchorOpen = openByMessage.get(anchorId) ?? 0;
  const anchorMsg = useMemo(
    () => item?.messages.find((m) => m.id === anchorId) ?? null,
    [item, anchorId],
  );
  const anchorSender = anchorMsg?.senderName || row.reportedName;

  /** The message ids the server actually returned. A report whose message falls
   *  outside the window has no row above it and so no action control — the
   *  Reports panel gives it one by re-anchoring on it. */
  const windowIds = useMemo(
    () => new Set((item?.messages ?? []).map((m) => m.id)),
    [item],
  );

  /** The reported messages of this case that are present in the window, which is
   *  what the case status is derived from below. */
  const reportedInWindow = useMemo(() => {
    const ids = new Set((item?.reports ?? []).map((rep) => rep.messageId));
    return (item?.messages ?? []).filter((m) => ids.has(m.id));
  }, [item]);

  // Same shape as ReviewModal's liveStatus, and the same reason for existing:
  // recomputed from THIS modal's freshly loaded item so the pill tracks the
  // modal's own actions, falling back to the row only while the detail loads.
  // Reading totalOpen alone flipped it to a green "No action" the instant a
  // takedown closed the last open report — the chip claiming nothing happened
  // directly above the message it had just removed.
  //
  // The ranking is the server's deriveDmCaseStatus, not derivePostStatus's:
  // outstanding work outranks a removal, because a conversation can have one
  // message down and two reports still open on the others, and retiring that
  // case would hide live work. So the two can never disagree with the queue row.
  const liveStatus: ModStatus = useMemo(() => {
    if (!item) return row.status;
    if (totalOpen > 0) return 'open';
    // The report resolutions are read first BECAUSE THEY ARE NOT WINDOWED: every
    // report on the conversation arrives, whereas a message removed outside the
    // ±25 window does not, and deriving only from the messages on screen would
    // hand back a green "No action" for a takedown the window simply cannot see.
    if (item.reports.some((rep) => rep.resolution === 'takedown')) return 'removed';
    if (reportedInWindow.some((m) => m.removed && m.removedKind === 'moderation')) return 'removed';
    if (reportedInWindow.some((m) => m.removed)) return 'author_deleted';
    return 'dismissed';
  }, [item, row.status, totalOpen, reportedInWindow]);

  const trimmedReason = reason.trim();

  const act = async (key: string, run: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await run();
      setReason('');
      // Both writes return a bare { success: true } and broadcast nothing, so
      // the only way to see the new state is to ask again.
      load();
      onChanged();
    } catch (e: any) {
      alert(e.response?.data?.message || 'The action could not be completed');
    } finally {
      setBusy(null);
    }
  };

  const doDismiss = (messageId: string) =>
    act(`dismiss:${messageId}`, () =>
      socialDmModerationAPI.dismiss({ projectId, messageId, ...(trimmedReason ? { note: trimmedReason } : {}) }));

  const doTakedown = (messageId: string) =>
    act(`takedown:${messageId}`, () =>
      socialDmModerationAPI.takedown({ projectId, messageId, reason: trimmedReason }));

  const counter = item?.counterReport;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 p-5">
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              {row.reportedName}
              <span className="font-mono text-xs font-normal text-gray-400">{row.reportedEmpCode}</span>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[liveStatus]?.cls || 'bg-gray-100 text-gray-600'}`}>
                {STATUS_META[liveStatus]?.label || liveStatus}
              </span>
            </div>
            <div className="mt-0.5 text-sm text-gray-500">
              Reported direct message · {item ? `${item.reports.length} report${item.reports.length === 1 ? '' : 's'}` : 'loading…'}
              {totalOpen > 0 ? ` · ${totalOpen} open` : ''}
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        {/* The disclosure, stated where the reading happens. The mobile app
            promises users that reviews are logged; this is the reviewer's half of
            that same promise, and the log row was written before these bytes
            arrived. */}
        <div className="flex shrink-0 items-start gap-2 border-b border-amber-100 bg-amber-50/60 px-5 py-2.5 text-[11px] text-amber-800">
          <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            This is a private conversation. Opening it has been recorded in the access log with your
            name, the conversation and the time. You are shown a bounded window around the reported
            message — not the full thread.
          </span>
        </div>

        {loading ? (
          <BlockLoader className="h-48" />
        ) : err ? (
          <div className="p-6 text-center text-sm text-red-600">{err}</div>
        ) : item ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <div className="grid gap-5 lg:grid-cols-[3fr,2fr]">
              {/* Left — the message window */}
              <div className="space-y-4">
                <div className="flex items-baseline justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Messages around the report ({item.messages.length})
                  </div>
                  <span className="text-[11px] text-gray-400">oldest first</span>
                </div>
                {item.messages.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-xs text-gray-400">
                    No messages in the window.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {item.messages.map((m) => (
                      <DmMessageRow
                        key={m.id}
                        projectId={projectId}
                        message={m}
                        isAnchor={m.id === anchorId}
                        openReports={openByMessage.get(m.id) ?? 0}
                        busy={busy != null}
                        reasonReady={!!trimmedReason}
                        onRemove={() => setConfirmRemoval({ messageId: m.id, who: m.senderName })}
                        onDismiss={() => doDismiss(m.id)}
                      />
                    ))}
                  </div>
                )}
                {/* Says the bound out loud. Without it a reviewer reading a
                    25-message window has no way to tell a short conversation
                    from a truncated one, and may conclude the context they were
                    not shown does not exist. */}
                <p className="text-[11px] text-gray-400">
                  Only the messages immediately either side of the reported one are shown. Removed
                  messages are kept visible — a taken-down message is the evidence. A report about a
                  message further out is listed on the right with a control to re-centre this window
                  on it; each re-centring is logged like any other read.
                </p>
              </div>

              {/* Right — who is in this conversation, and who complained */}
              <div className="space-y-4">
                <section>
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    <Users className="h-3.5 w-3.5" /> Participants
                  </div>
                  <div className="space-y-1.5">
                    {item.participants.map((p) => (
                      <div key={p.empCode} className="rounded-lg border border-gray-100 p-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-gray-800">{p.name}</span>
                          <span className="shrink-0 font-mono text-[11px] text-gray-400">{p.empCode}</span>
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {[p.designation, p.storeName].filter(Boolean).join(' · ') || 'No store on record'}
                        </div>
                        {p.leftAt && <div className="mt-0.5 text-[11px] text-gray-400">Left {fmtDate(p.leftAt)}</div>}
                      </div>
                    ))}
                  </div>
                </section>

                {/* The single most useful thing on this screen. Two symmetric
                    reports look like a mutual spat; who filed FIRST is usually
                    what separates a victim from a retaliating harasser, and it
                    is invisible unless stated. */}
                {counter?.exists && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      The other party also filed a report in this conversation
                      {counter.filedAfterPrimary
                        ? ' AFTER the first one — a counter-report, which is a common retaliation pattern.'
                        : ' BEFORE the first one, so the report you are looking at is itself the counter-report.'}
                      {' '}Read the order before treating this as a mutual dispute.
                    </span>
                  </div>
                )}

                <section>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Reports ({item.reports.length}{totalOpen ? ` · ${totalOpen} open` : ''})
                  </div>
                  {item.reports.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-xs text-gray-400">No reports on record.</div>
                  ) : (
                    <div className="space-y-2">
                      {item.reports.map((rep) => (
                        <div key={rep.id} className="rounded-lg border border-gray-100 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-gray-800">{rep.reporterName}</div>
                              <div className="font-mono text-[11px] text-gray-400">{rep.reporterEmpCode}</div>
                            </div>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              rep.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {rep.status === 'open' ? 'Open' : 'Actioned'}
                            </span>
                          </div>
                          <div className="mt-1.5 text-sm text-gray-700">{reasonLabel(rep.reasonCode)}</div>
                          {rep.reasonText && <p className="mt-0.5 whitespace-pre-wrap text-xs text-gray-600">“{rep.reasonText}”</p>}
                          {/* The body AS REPORTED. Survives an edit or a
                              takedown, so the record of what was complained
                              about does not vanish with the message. */}
                          {rep.reportedBody && (
                            <p className="mt-1.5 rounded bg-gray-50 p-2 text-xs italic text-gray-600">“{rep.reportedBody}”</p>
                          )}
                          <div className="mt-1.5 text-[11px] text-gray-400">{fmtDateTime(rep.createdAt)}</div>
                          {/* A case whose reports span more than the ±25-message
                              window has reports whose message is not on the left,
                              so nothing on this screen could dismiss or remove
                              them and the queue row could never be cleared. Say
                              so, and offer the way to reach it: re-centring the
                              window on a message that is itself reported here. */}
                          {!windowIds.has(rep.messageId) && (
                            <div className="mt-1.5 flex flex-wrap items-center justify-between gap-1.5 rounded bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                              <span>
                                Outside the messages shown
                                {rep.reportedSeq != null ? ` (message #${rep.reportedSeq})` : ''}.
                              </span>
                              <button
                                type="button"
                                onClick={() => setAnchorId(rep.messageId)}
                                disabled={busy != null}
                                title="Re-centre the window on this reported message so it can be dismissed or removed"
                                className="shrink-0 rounded border border-amber-300 px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-40"
                              >
                                Show in context
                              </button>
                            </div>
                          )}
                          {rep.resolution && (
                            <div className="mt-1.5 border-t border-gray-100 pt-1.5 text-[11px] text-gray-500">
                              {rep.resolution === 'takedown' ? 'Message removed' : 'No action taken'}
                              {rep.resolvedBy ? ` by ${rep.resolvedBy}` : ''}
                              {rep.resolvedAt ? ` · ${fmtDate(rep.resolvedAt)}` : ''}
                              {rep.resolutionNote ? <div className="mt-0.5 text-gray-600">“{rep.resolutionNote}”</div> : null}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        ) : null}

        {/* Footer — the reason, then the decision on the REPORTED message */}
        {item && (
          <div className="shrink-0 border-t border-gray-100 p-4">
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Removal reason <span className="text-red-600">*</span>
              <span className="ml-1 font-normal text-gray-400">
                required to remove a message — it is the record shown if the employee escalates, and it
                is also attached as the note on a dismissal. A DM removal cannot be undone from here.
              </span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Why is this message being removed?"
              className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              {/* Names the granularity, because both buttons act on ONE message
                  and the queue row is a whole conversation. Without this a
                  reviewer dismisses the anchor, sees the row still sitting in
                  the Open queue, and concludes the action failed. */}
              <span className="text-[11px] text-gray-400">
                Acting on the reported message from {anchorSender}
                {totalOpen > anchorOpen ? ` · ${totalOpen - anchorOpen} more open report(s) on other messages above` : ''}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => doDismiss(anchorId)}
                  disabled={busy != null || anchorOpen === 0}
                  title={anchorOpen === 0 ? 'No open reports left on this message' : 'Close every open report on this message with no action'}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {busy === `dismiss:${anchorId}` ? 'Dismissing…' : `Dismiss${anchorOpen ? ` (${anchorOpen})` : ''}`}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRemoval({ messageId: anchorId, who: anchorSender })}
                  disabled={busy != null || !trimmedReason || !anchorMsg || anchorMsg.removed}
                  title={
                    !anchorMsg ? 'The reported message is not in this window'
                      : anchorMsg.removed ? 'This message is already removed'
                      : !trimmedReason ? 'Enter a removal reason first'
                      : 'Remove this message from the conversation'
                  }
                  className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <ShieldOff className="h-4 w-4" /> {busy === `takedown:${anchorId}` ? 'Removing…' : 'Take down'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={confirmRemoval != null}
        onClose={() => setConfirmRemoval(null)}
        onConfirm={() => {
          const target = confirmRemoval;
          setConfirmRemoval(null);
          if (target) doTakedown(target.messageId);
        }}
        isSubmitting={busy != null && busy.startsWith('takedown:')}
        title="Remove message"
        entityLabel="message from"
        entityName={confirmRemoval?.who ?? ''}
        // Unlike a post takedown, there is NO restore route for a DM, so the
        // reversibility copy the posts modal uses would be a straight lie here.
        consequenceNote="Both participants will see it replaced with “Message unavailable”. There is no restore for a direct message — this cannot be undone from ChannelPlex."
        confirmLabel="Remove"
      />
    </div>
  );
}

// ── One message in the window ────────────────────────────────────────────────
// The whole window is rendered, removed messages included, for the same reason
// the comment thread is: judging a reported line needs the lines around it, and
// seeing what has already been actioned stops a second reviewer re-litigating it.
// ── The attachment on a reported DM, as evidence ─────────────────────────────
/**
 * THE MESSAGE THAT IS HARDEST TO REVIEW IS THE ONE WITH NO WORDS IN IT.
 *
 * A voice note stores an empty `content`, so until this existed the review window
 * rendered a reported recording as a blank grey box with a REPORTED badge — and
 * takedown works on it regardless, which made "remove" the only defensible answer to
 * something nobody could examine. A photo was equally invisible.
 *
 * The bytes come from /social-dm-moderation/media, which serves REMOVED content on
 * purpose (the removed recording is the evidence), logs the read before it answers,
 * and re-checks the moderator's own grant on every request. Fetched as a blob because
 * neither `<img src>` nor `<audio src>` sends the Authorization header.
 *
 * Native `<audio controls>`, not a custom transport. A moderator needs play, scrub and
 * volume, and the browser's own player is the one that is already keyboard-accessible.
 */
function DmAttachmentEvidence({ projectId, message }: { projectId: number; message: DmMessage }) {
  const isVoice = message.kind === 'voice';
  const path = socialDmModerationAPI.mediaPath({ projectId, messageId: message.id });
  const { url, loading, failed } = useAuthedMediaUrl(path);

  const meta = [
    isVoice ? 'Voice note' : 'Photo',
    message.attachmentDurationMs ? fmtMs(message.attachmentDurationMs) : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="mt-1.5">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] text-gray-500">
        {isVoice ? <Volume2 className="h-3.5 w-3.5" /> : <Paperclip className="h-3.5 w-3.5" />}
        {meta}
      </div>
      {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      {/* The only coloured thing here, and only when it is the exception: the
          attachment could not be fetched, so nothing on screen should look like it
          was reviewed. */}
      {failed && (
        <span className="text-[11px] text-red-600">
          Attachment could not be loaded — decide on the reports, not on this.
        </span>
      )}
      {url && (isVoice
        ? <audio controls preload="metadata" src={url} className="h-8 w-full max-w-xs" />
        : <img src={url} alt="Reported attachment" className="max-h-64 rounded border border-gray-200" />)}
    </div>
  );
}

function DmMessageRow({ projectId, message, isAnchor, openReports, busy, reasonReady, onRemove, onDismiss }: {
  projectId: number;
  message: DmMessage;
  isAnchor: boolean;
  openReports: number;
  busy: boolean;
  reasonReady: boolean;
  onRemove: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className={`rounded-lg p-2.5 text-sm ${
      isAnchor ? 'bg-amber-50 ring-1 ring-amber-200'
        : message.isReported ? 'bg-amber-50/40'
        : message.removed ? 'bg-gray-50 opacity-70'
        : 'bg-gray-100'
    }`}>
      <div className="mb-0.5 flex items-center justify-between gap-2 text-[11px] text-gray-500">
        <span className="truncate font-medium text-gray-700">
          {message.senderName}
          <span className="ml-1.5 font-mono text-[10px] text-gray-400">{message.senderEmpCode}</span>
          {message.isReported && <span className="ml-1.5 rounded bg-amber-200 px-1 text-[10px] font-semibold text-amber-800">REPORTED</span>}
          {openReports > 0 && <span className="ml-1.5 text-rose-600">{openReports} open</span>}
        </span>
        {/* Epoch milliseconds, not ISO — see fmtEpoch. */}
        <span className="shrink-0">{fmtEpoch(message.createdAt)}</span>
      </div>
      <p className={`whitespace-pre-wrap ${message.removed ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
        {message.content}
      </p>
      {/* Rendered for a REMOVED message too. The bytes a moderator took down are the
          record of what they took down, and this window is also how a removal is
          reviewed months later. */}
      {message.hasAttachment && <DmAttachmentEvidence projectId={projectId} message={message} />}
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="text-[11px] text-red-600">
          {message.removed && (message.removedKind === 'moderation'
            ? `Removed by ${message.removedBy || 'a moderator'}${message.removedReason ? ` — “${message.removedReason}”` : ''}`
            : 'Deleted by the sender')}
        </span>
        {/* Per-message, because both endpoints key on a message id. The anchor's
            pair lives in the footer as the primary path, so it is not repeated
            here — only the rest of the window carries its own controls. */}
        {!isAnchor && (
          <div className="flex shrink-0 items-center gap-1.5">
            {openReports > 0 && (
              <button
                type="button"
                onClick={onDismiss}
                disabled={busy}
                title="Close every open report on this message with no action"
                className="rounded border border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Dismiss
              </button>
            )}
            {!message.removed && (
              <button
                type="button"
                onClick={onRemove}
                disabled={busy || !reasonReady}
                title={reasonReady ? 'Remove this message' : 'Enter a removal reason below first'}
                className="rounded border border-red-200 px-2 py-0.5 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
